import { QueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  DefaultUserPreferencesError,
  DefaultUserPreferencesResponse,
  InitUserPreferencesError,
  PatchUserPreferencesError,
  PatchUserPreferencesRequest,
  PatchUserPreferencesResponse,
  UserPreferencesError,
  UserPreferencesResponse,
} from '@/api/model';

const KEY = {
  GET_USER_PREFERENCES: 'GET_USER_PREFERENCES',
} as const;

const request = {
  getDefaultPreferences: async () => {
    const { data } = await fetchClient.FCT.get<DefaultUserPreferencesResponse>(
      ENDPOINTS.FCT.ME.PREFERENCES.DEFAULTS,
    );

    return data;
  },
  getUserPreferences: async (signal?: AbortSignal) => {
    const { data } = await fetchClient.FCT.get<UserPreferencesResponse>(
      ENDPOINTS.FCT.ME.PREFERENCES.BASE,
      { signal },
    );

    return data;
  },
  initUserPreferences: async () => {
    await fetchClient.FCT.post<void>(ENDPOINTS.FCT.ME.PREFERENCES.INIT);
  },
  patchUserPreferences: async ({ payload }: PatchUserPreferencesRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchUserPreferencesResponse>(
      ENDPOINTS.FCT.ME.PREFERENCES.BASE,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetUserPreferences: (
    config?: Partial<UseQueryOptions<UserPreferencesResponse, UserPreferencesError>>,
  ) =>
    useQuery<UserPreferencesResponse, UserPreferencesError>({
      queryFn: ({ signal }) => request.getUserPreferences(signal),
      queryKey: [KEY.GET_USER_PREFERENCES],
      // avoid refetching preferences during re-renders
      staleTime: Infinity,
      ...config,
    }),
};

const mutation = {
  useInitUserPreferences: (queryClient?: QueryClient) =>
    useMutation<void, InitUserPreferencesError, void>({
      mutationFn: request.initUserPreferences,
      onSuccess: () => {
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: [KEY.GET_USER_PREFERENCES] });
        }
      },
    }),
  usePatchUserPreferences: (queryClient?: QueryClient) =>
    useMutation<
      PatchUserPreferencesResponse,
      PatchUserPreferencesError,
      PatchUserPreferencesRequest
    >({
      mutationFn: request.patchUserPreferences,
      onSuccess: (data) => {
        if (queryClient) {
          queryClient.setQueryData([KEY.GET_USER_PREFERENCES], data);
        }
      },
    }),
  useResetUserPreferences: () =>
    useMutation<DefaultUserPreferencesResponse, DefaultUserPreferencesError, void>({
      mutationFn: request.getDefaultPreferences,
    }),
};

export const userPreference = {
  KEY,
  mutation,
  query,
  request,
};
